import { formatInTimeZone } from 'date-fns-tz';
import moment from 'moment';

import { DayAvailabilityResponse } from '../../redux/ellipsisApi';

/**
 * Returns excluded date ranges (as ISO#ISO pairs) for days not in dateArray for the given month/year
 */
export const getExcludedDates = (
  monthIndex: number,
  year: number,
  dateArray: string[]
): string[] => {
  const startDate = moment().month(monthIndex).year(year).startOf('month');
  const endDate = startDate.clone().add(1, 'month').endOf('month');
  const excluded: string[] = [];
  for (
    let loopDate = startDate.clone();
    loopDate.isBefore(endDate);
    loopDate.add(1, 'day')
  ) {
    if (dateArray.indexOf(loopDate.format('DD:MM:YYYY')) === -1) {
      const startDay = loopDate.clone().startOf('day');
      const endDay = loopDate.clone().endOf('day');
      excluded.push(`${startDay.toISOString()}#${endDay.toISOString()}`);
    }
  }
  return excluded;
};

export const makeLocalAppearAsUTC = (value: Date | null): Date | null => {
  if (!value) return null;
  const d = new Date(value as Date);
  if (isNaN(d.getTime())) return d;
  const shown = new Date(0);
  shown.setFullYear(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  shown.setHours(
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
    d.getUTCMilliseconds()
  );
  return shown;
};

export const localToUTC = (value: Date | null): Date | null => {
  if (!value) return null;
  const d = new Date(value as Date);
  if (isNaN(d.getTime())) return d;
  const stored = new Date(0);
  stored.setUTCFullYear(d.getFullYear(), d.getMonth(), d.getDate());
  stored.setUTCHours(
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds()
  );
  return stored;
};

/**
 * EM-1119: the offer date picker fetches availability one month "chunk" at a
 * time as it's navigated, and each chunk's response only covers a rolling
 * window a few months wide. A fresh chunk used to *replace* whatever chunk
 * was already in state, so as soon as the picker moved past the first
 * fetched window, the earlier days fell out of the available-dates set —
 * making every date beyond that first window look unavailable, since
 * shouldDisableDate requires the whole range between the selected start and
 * a candidate day to be present. Merge chunks by date instead of replacing,
 * so previously fetched months stay selectable. Where two chunks both cover
 * the same day, the second (presumed more recent) chunk's data wins.
 */
export const mergeDayAvailability = (
  prev: DayAvailabilityResponse | undefined,
  next: DayAvailabilityResponse | undefined
): DayAvailabilityResponse | undefined => {
  if (!next) return prev;
  if (!prev) return next;

  type DayAvailability = NonNullable<
    DayAvailabilityResponse['availableDates']
  >[number];
  const byDate = new Map<string, DayAvailability>();
  for (const day of prev.availableDates ?? []) {
    if (day.date) byDate.set(day.date, day);
  }
  for (const day of next.availableDates ?? []) {
    if (day.date) byDate.set(day.date, day);
  }

  const availableDates = Array.from(byDate.values()).sort((a, b) =>
    (a.date ?? '').localeCompare(b.date ?? '')
  );

  const startDate =
    prev.startDate && next.startDate
      ? prev.startDate < next.startDate
        ? prev.startDate
        : next.startDate
      : (next.startDate ?? prev.startDate);
  const endDate =
    prev.endDate && next.endDate
      ? prev.endDate > next.endDate
        ? prev.endDate
        : next.endDate
      : (next.endDate ?? prev.endDate);

  return { startDate, endDate, availableDates };
};

/**
 * EM-1119 (follow-up): the offer date picker's availability fetch used to be
 * driven off a single query hook keyed on "whatever month the calendar was
 * last navigated to" - opening the picker for the first time never fetched
 * anything for the months already on screen, so the calendar showed no
 * availability at all until the user paged forward and back once.
 *
 * The picker always shows exactly two months (the visible month and the
 * next), so the fix doesn't need imperative/dynamic fetching - the
 * generated query hook can just be called twice, unconditionally, once per
 * visible month. RTK Query's own cache already dedupes a month that's
 * already been fetched; the only thing still worth gating per-hook is
 * this - months entirely before the current calendar month are never
 * requested, since bookings can't be made in the past.
 */
export const isCalendarMonthInPast = (
  month: number,
  year: number,
  today: Date = new Date()
): boolean => {
  const monthStart = new Date(year, month, 1);
  const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return monthStart < todayMonthStart;
};

/** Returns true if any day in [startDate, endDate] is unavailable (not found in availableDaysString). */
export const hasUnavailableDateBetween = (
  startDate: Date,
  endDate: Date,
  availableDays: Set<string>,
  availableHalfDays: Set<string>
): boolean => {
  // Normalize to UTC midnight to avoid TZ drift
  const start = new Date(
    Date.UTC(
      startDate.getUTCFullYear(),
      startDate.getUTCMonth(),
      startDate.getUTCDate()
    )
  );
  const end = new Date(
    Date.UTC(
      endDate.getUTCFullYear(),
      endDate.getUTCMonth(),
      endDate.getUTCDate()
    )
  );

  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const queryDate = formatInTimeZone(d, 'UTC', 'yyyy-MM-dd');
    const hasDay = availableDays.has(queryDate);
    if (!hasDay) return true;
    const hasHalfDay =
      start.getTime() !== d.getTime() && availableHalfDays.has(queryDate);
    if (hasHalfDay) return true;
    // if start is half day, every day that's not start is unavailable
    if (
      availableHalfDays.has(formatInTimeZone(start, 'UTC', 'yyyy-MM-dd')) &&
      queryDate !== formatInTimeZone(start, 'UTC', 'yyyy-MM-dd')
    )
      return true;
  }
  return false;
};

/** Returns true if any day in [startDate, endDate] is a half-day according to availableHalfDaysString */
export const isHalfDayBetween = (
  startDate: Date,
  endDate: Date,
  availableHalfDaysString: Set<string>
): boolean => {
  const start = moment(startDate).utc().startOf('day').toDate();
  const end = moment(endDate).utc().startOf('day').toDate();
  const daysBetween = moment.duration(moment(end).diff(moment(start))).asDays();
  for (let i = 0; i <= daysBetween; i++) {
    const day = moment(start).add(i, 'days').toDate();
    if (availableHalfDaysString.has(moment(day).format('YYYY-MM-DD'))) {
      return true;
    }
  }
  return false;
};
